import { t } from 'i18next';

import type { Salutation } from 'features/conversation-preferences/services/conversation-preferences-service';
import {
  isAcceptedPreferenceText,
  SALUTATION_TEXT_MAX_LENGTH,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import type { OnboardingProgress } from 'state/store/onboarding/use-onboarding-store';

export type SalutationGuess = 'frau' | 'herr' | 'unclear';

type SalutationAnswer = Pick<
  OnboardingProgress,
  'addressForm' | 'givenName' | 'familyName' | 'salutation' | 'salutationText' | 'name'
>;

export const ONBOARDING_SALUTATION_OPTIONS = [
  { value: 'frau', key: 'ONBOARDING_SALUTATION_FRAU', defaultValue: 'Ms {{familyName}}' },
  { value: 'herr', key: 'ONBOARDING_SALUTATION_HERR', defaultValue: 'Mr {{familyName}}' },
  { value: 'none', key: 'ONBOARDING_SALUTATION_NONE', defaultValue: 'No salutation' },
] as const;

/** Formal mode asks only when a family name is known and the guess left it open. */
export const needsSalutationChoice = (answer: SalutationAnswer) =>
  answer.addressForm === 'formal' && Boolean(answer.familyName) && !answer.salutation;

/** "Frau Müller", "Dr. Müller", or the full name for "none". Undefined when Buddy uses the
 *  given name, as in informal mode or Sie with only a first name. */
export function onboardingSalutationName(answer: SalutationAnswer): string | undefined {
  const familyName = answer.familyName?.trim();
  if (answer.addressForm !== 'formal' || !familyName) return undefined;
  const option = ONBOARDING_SALUTATION_OPTIONS.find((item) => item.value === answer.salutation);
  if (option && option.value !== 'none') {
    return t(option.key, { defaultValue: option.defaultValue, familyName });
  }
  if (answer.salutation === 'custom' && answer.salutationText?.trim()) {
    return `${answer.salutationText.trim()} ${familyName}`;
  }
  if (answer.salutation === 'none') {
    return [answer.givenName ?? answer.name, familyName].filter(Boolean).join(' ');
  }
  return undefined;
}

/** How Buddy calls the user in a line: the salutation in formal mode, else the given name. */
export const onboardingAddressName = (answer: SalutationAnswer) =>
  onboardingSalutationName(answer) ?? answer.givenName ?? answer.name ?? '';

/** Salutation words typed instead of picked map to the chips. */
const SALUTATION_WORDS: Record<string, Exclude<Salutation, 'custom' | 'none'>> = {
  frau: 'frau',
  herr: 'herr',
  madame: 'frau',
  monsieur: 'herr',
  signora: 'frau',
  signor: 'herr',
  ms: 'frau',
  mrs: 'frau',
  mr: 'herr',
};

/** A typed salutation such as "Dr. Keller" → custom "Dr.". Null when the preference would refuse
 *  it (over 40 characters, `<>`, a backtick or invisible characters), so it is never sent. */
export function parseSalutationText(
  text: string,
  familyName?: string
): Pick<OnboardingProgress, 'salutation' | 'salutationText'> | null {
  let value = text.trim().replaceAll(/\s+/g, ' ');
  const family = familyName?.trim();
  if (family && value.toLowerCase().endsWith(family.toLowerCase())) {
    value = value.slice(0, value.length - family.length).trim();
  }
  if (!isAcceptedPreferenceText(value, SALUTATION_TEXT_MAX_LENGTH)) return null;
  const word = SALUTATION_WORDS[value.toLowerCase().replace(/\.$/, '')];
  return word ? { salutation: word } : { salutation: 'custom', salutationText: value };
}
